import { SiteInfo } from "@/globals";
import { getProducts, getProductsCount, ProductResponse } from "@/js/repository/ProductRepository";
import { CategoryResponse, getCategoriesByProducts, getSecondCategoriesAllByProducts } from "@/js/repository/CategoryRepository";
import { getCategoryIdFromSlug, getCategoryPath, getProductPath } from "@/js/utils/stringUtils";
import Head from "next/head";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import axios from "axios";
import { AdSenseBanner } from "@/components/app/AdSenseBanner";
import { useIsMobile } from "@/lib/useIsMobile";
import { ProductList } from "../products";
import { buildKeywordContent } from "@/lib/seo";

const truncateTitle = (title: string, maxLength: number = 70) => {
  return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
};

const getFullTitle = (categoryName: string, siteName: string) => {
  const separator = ' | ';
  const maxTitleLength = 70;
  return `${truncateTitle(categoryName + separator + siteName, maxTitleLength)}`;
};

export async function getServerSideProps(context: any) {
  const { locale } = context;
  const { slug } = context.params as { slug: string };
  const { page } = context.query;
  const siteUrl = `https://${context.req.headers.host}`;

  const categoryId = getCategoryIdFromSlug(slug);
  if (categoryId === null) {
    return { notFound: true };
  }

  const pageNumber = parseInt(String(page)) || 1;
  const pageSize = 40 * pageNumber;

  const categories = await getCategoriesByProducts(locale);
  const secondCategoriesAll = await getSecondCategoriesAllByProducts(locale);

  const categoryInfo = categories.find((c: CategoryResponse) => c.id === categoryId)
    ?? secondCategoriesAll.find((c: CategoryResponse) => c.id === categoryId);

  if (!categoryInfo) {
    return { notFound: true };
  }

  const canonicalPath = getCategoryPath(categoryId, categoryInfo.name);
  const canonicalSlug = canonicalPath.replace(/^\/category\//, '');
  const decodedSlug = decodeURIComponent(slug);
  if (decodedSlug !== canonicalSlug) {
    // Location 헤더는 ASCII만 허용하므로 slug를 인코딩 (한글 등 비ASCII 문자 보정)
    const encodedSlug = encodeURIComponent(canonicalSlug);
    const dest = pageNumber > 1 ? `/category/${encodedSlug}?page=${pageNumber}` : `/category/${encodedSlug}`;
    return {
      redirect: {
        destination: dest,
        permanent: true,
      },
    };
  }

  const products = await getProducts(locale, 1, pageSize, categoryId);
  const productsCount = await getProductsCount(locale, categoryId);
  const secondCategories = secondCategoriesAll.filter(
    (c: CategoryResponse) => c.parent_id === categoryInfo.parent_id || c.parent_id === categoryInfo.id
  );
  const hasNext = productsCount > pageNumber * 40;

  return {
    props: {
      categories,
      secondCategories,
      products,
      category: String(categoryId), // API still uses string for compatibility
      categoryInfo,
      hasNext,
      pageNumber,
      siteUrl,
      productsCount,
    },
  };
}

export default function CategoryPage(props: any) {
  const { t } = useTranslation();
  const router = useRouter();
  const locale = router.locale;
  const isMobile = useIsMobile();

  const [productList, setProductList] = useState<ProductResponse[]>(props.products);
  const [hasNext, setHasNext] = useState<boolean>(props.hasNext);
  const [page, setPage] = useState<number>(props.pageNumber || 1);
  const [loading, setLoading] = useState(false);

  const { secondCategories, categoryInfo, categories } = props;
  const categoryPath = getCategoryPath(Number(props.category), categoryInfo?.name ?? '');

  useEffect(() => {
    setProductList(props.products);
    setHasNext(props.hasNext);
    setPage(props.pageNumber);
  }, [props.products, props.hasNext, props.pageNumber]);

  async function loadMore() {
    const nextPage = page + 1;
    setLoading(true);
    try {
      const response = await axios.post(`/api/products/products`, {
        page: nextPage,
        category: props.category,
        locale,
      });
      if (response.status === 200) {
        const newProducts = response.data.products;
        setProductList([...productList, ...newProducts]);
        setPage(nextPage);
        setHasNext(response.data.hasNext);
        const nextUrl = nextPage > 1 ? `${categoryPath}?page=${nextPage}` : categoryPath;
        router.push(nextUrl, undefined, { shallow: true });
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Head>
        <title>{getFullTitle(categoryInfo.name, SiteInfo.siteName)}</title>
        <meta name="description" content={`${categoryInfo.name} - ${t("siteDescription")}`} />
        <meta
          name="keywords"
          content={buildKeywordContent([
            ...SiteInfo.primaryKeywords,
            categoryInfo.parent_name,
            categoryInfo.name,
            '카테고리',
            '할인',
            '핫딜',
          ])}
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href={
            props.pageNumber > 1
              ? `${props.siteUrl}${categoryPath}?page=${props.pageNumber}`
              : `${props.siteUrl}${categoryPath}`
          }
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${props.siteUrl}${categoryPath}`} />
        <meta property="og:title" content={getFullTitle(categoryInfo.name, SiteInfo.siteName)} />
        <meta property="og:description" content={`${categoryInfo.name} - ${t("siteDescription")}`} />
        <meta property="og:image" content={`${props.siteUrl}/logo.png`} />
        <meta property="og:site_name" content={SiteInfo.siteName} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`${props.siteUrl}/logo.png`} />
        <meta name="twitter:title" content={getFullTitle(categoryInfo.name, SiteInfo.siteName)} />
        <meta name="twitter:description" content={`${categoryInfo.name} - ${t("siteDescription")}`} />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              {
                "@context": "https://schema.org",
                "@type": "CollectionPage",
                "name": truncateTitle(categoryInfo.name, 70 - (SiteInfo.siteName.length + 3)),
                "description": `${categoryInfo.name} - ${t("siteDescription")}`,
                "url": `${props.siteUrl}${categoryPath}`,
                "numberOfItems": props.productsCount,
                "category": categoryInfo.name,
              },
              null,
              2
            ),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": props.siteUrl },
                { "@type": "ListItem", "position": 2, "name": categoryInfo.parent_name ?? categoryInfo.name, "item": `${props.siteUrl}${categoryPath}` },
              ],
            }, null, 2),
          }}
        />
        {props.products?.length > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "ItemList",
                "numberOfItems": props.productsCount,
                "itemListElement": props.products.slice(0, 20).map((p: ProductResponse, i: number) => ({
                  "@type": "ListItem",
                  "position": i + 1,
                  "url": `${props.siteUrl}${getProductPath(p.detail.product_id, p.detail.product_title)}`,
                  "name": p.detail.product_title?.substring(0, 100),
                })),
              }, null, 2),
            }}
          />
        )}
      </Head>

      <section className="mb-6 rounded-lg border border-black/10 bg-white p-5 shadow-sm sm:p-6">
        <p className="text-xs font-black uppercase tracking-normal text-[#fd384f]">{t("curatedByProsense")}</p>
        <h1 className="mt-2 text-2xl font-black text-[#101820] sm:text-3xl">{categoryInfo.parent_name ?? categoryInfo.name}</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">{t("categoryIntro")}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-gray-500">
          <span className="rounded-md bg-[#f7f4ef] px-3 py-1.5 font-semibold text-[#101820]">{props.productsCount} {t("products")}</span>
          {(props.pageNumber > 1 || props.productsCount > 40) && (
            <span>
              {t("showingCount", {
                from: (props.pageNumber - 1) * 40 + 1,
                to: Math.min(props.pageNumber * 40, props.productsCount),
                total: props.productsCount,
              })}
            </span>
          )}
        </div>
      </section>

      {secondCategories.length > 0 && (
        <ul className="mb-6 flex flex-wrap gap-2">
          <li
            key={categoryInfo.parent_id ?? categoryInfo.id}
            className={`rounded-lg px-3 py-2 text-sm font-semibold ${categoryInfo.parent_id === undefined ? 'bg-[#101820] text-white' : 'bg-white text-[#101820] border border-black/10'}`}
          >
            <a href={getCategoryPath(categoryInfo.parent_id ?? categoryInfo.id, categoryInfo.parent_name ?? categoryInfo.name)}>
              {t("all")}
            </a>
          </li>
          {secondCategories.map((c: CategoryResponse) => (
            <li
              key={c.id}
              className={`rounded-lg px-3 py-2 text-sm font-semibold ${categoryInfo.id === c.id ? 'bg-[#101820] text-white' : 'bg-white text-[#101820] border border-black/10'}`}
            >
              <a href={getCategoryPath(c.id, c.name)}>{c.name}</a>
            </li>
          ))}
        </ul>
      )}

      <div className="my-6">
        <AdSenseBanner id="category-content-top" slot="4220411723" height={isMobile ? 100 : 120} />
      </div>

      <ProductList products={productList} />

      <div className="my-6">
        <AdSenseBanner id="category-content-bottom" slot="4220411723" height={isMobile ? 180 : 240} />
      </div>

      {hasNext && (
        <div className="text-center mt-12">
          <button
            className="rounded-lg bg-[#101820] px-14 py-3 text-md font-bold text-white transition hover:bg-[#fd384f] disabled:opacity-70"
            onClick={() => loadMore()}
            disabled={loading}
            title={t("showMore")}
          >
            {loading ? t("loadingMore") : t("showMore")}
          </button>
        </div>
      )}

      {categories?.length > 1 && (
        <section className="mt-12 border-t border-black/10 pt-8" aria-label={t("otherCategories")}>
          <h2 className="text-lg font-bold mb-3">{t("otherCategories")}</h2>
          <ul className="flex flex-wrap gap-2">
            {categories
              .filter(
                (c: CategoryResponse) =>
                  !c.parent_id && c.id !== categoryInfo?.parent_id && c.id !== categoryInfo?.id
              )
              .slice(0, 6)
              .map((c: CategoryResponse) => (
                <li key={c.id}>
                  <a
                    href={getCategoryPath(c.id, c.name)}
                    className="rounded-lg border border-black/10 bg-white px-3 py-2 text-sm font-semibold hover:border-[#fd384f]/40 hover:text-[#fd384f]"
                  >
                    {c.name}
                  </a>
                </li>
              ))}
          </ul>
        </section>
      )}
    </>
  );
}
